/**
 * nativeApp — 原生 App 壳（Capacitor / iOS）与浏览器的唯一分叉点。
 *
 * 浏览器里这里的每个函数都是零副作用：`isNativeApp()` 恒为 false，`apiOrigin()` 恒为空串，
 * 请求头为空对象，Keychain 读写不会被调用。原生分支只在 `Capacitor.isNativePlatform()` 为真时走：
 *
 * - 原生 App 固定连接国际服，进场界面固定英文，不读设备语言或历史选服作为运行配置。
 * - 登录态：refresh token 不能走跨站 HttpOnly cookie，请求带 `X-TCW-Client: ios/<build>` 让后端把它放
 *   响应体，这里存进 iOS Keychain，刷新时放请求体送回。退出登录把 refresh token 连同本机推送设备 token 送给
 *   `/auth/logout` 停用设备；请求没送到就留在 Keychain 里（pending_logout）下次重试。
 * - 站内相对地址：服务端返回的 `/api/...` 图片地址在原生壳里经 resolveApiUrl 指回 API 服务器。
 * - 铺满整屏：运行时把 viewport 改成 `viewport-fit=cover`，顶部安全区由 `--px-safe-top` 让出。
 */

import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { StatusBar, Style } from '@capacitor/status-bar'
import { SecureStorage } from '@aparajita/capacitor-secure-storage'

export const NATIVE_CLIENT_HEADER = 'X-TCW-Client'
const REFRESH_TOKEN_KEY = 'refresh_token'
// 退出登录时要送给服务端、但请求失败了的凭据：留在 Keychain 里下次启动/回前台重试（api.ts 的 flushPendingNativeLogout）
const PENDING_LOGOUT_KEY = 'pending_logout'
/** 本机 APNs 设备 token（localStorage）；由 nativePush 在注册回调里写，退出登录时由 api.ts 取走并清掉 */
export const PUSH_DEVICE_TOKEN_KEY = 'push_device_token'

export interface NativeServer {
  id: 'en'
  origin: string
  locale: 'en'
}

// 本机模拟器联调用显式构建参数覆盖；发布构建不设置该参数。
const DEV_API_ORIGIN = String(import.meta.env.VITE_NATIVE_API_ORIGIN || '').replace(/\/$/, '')
const INTERNATIONAL_SERVER: NativeServer = {
  id: 'en', origin: DEV_API_ORIGIN || 'https://play.slowvale.com', locale: 'en',
}

export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform()
}

/** 原生唯一服务器，任何设备语言及旧选服值都不能改变它。 */
export function nativeServer(): NativeServer {
  if (!isNativeApp()) throw new Error('nativeServer 只在原生 App 里可用')
  return INTERNATIONAL_SERVER
}

/** API 绝对 origin：浏览器恒为空串（保持 `/api` 相对路径），原生为所选服务器。 */
export function apiOrigin(): string {
  return isNativeApp() ? nativeServer().origin : ''
}

/**
 * 服务端给出的站内相对地址（`/api/...` 的图片、二维码等）在原生壳里要指回 API 服务器：包内页面的 origin 是
 * `capacitor://localhost`，相对路径会去包里找。浏览器里原样返回；绝对地址原样返回。
 */
export function resolveApiUrl(url: string): string {
  if (!url.startsWith('/') || url.startsWith('//')) return url
  return `${apiOrigin()}${url}`
}

export function storedPushDeviceToken(): string | null {
  return localStorage.getItem(PUSH_DEVICE_TOKEN_KEY)
}

/** 推送和深链带来的落地路径只接受站内 /world/ 路由（GameView 的路径与 query），其他一律忽略（不跳） */
export function inAppLandingPath(path: unknown): string | null {
  if (typeof path !== 'string' || !path.startsWith('/world/') || path.includes('//') || path.includes('://')) return null
  return path
}

let nativeBuild: string | null = null

/** 原生 build 号（CFBundleVersion，正整数）；浏览器或未初始化时报错。热更新用它判断包是否兼容当前壳。 */
export function nativeBuildNumber(): number {
  if (!isNativeApp()) throw new Error('nativeBuildNumber 只在原生 App 里可用')
  if (!nativeBuild) throw new Error('原生 App 尚未完成 initNativeApp，拿不到 build 号')
  if (!/^\d+$/.test(nativeBuild)) throw new Error(`原生 build 号必须是正整数，实际为 ${nativeBuild}`)
  return Number(nativeBuild)
}

/** 认证接口用的原生客户端标识头；浏览器为空对象。 */
export function nativeRequestHeaders(): Record<string, string> {
  if (!isNativeApp()) return {}
  if (!nativeBuild) throw new Error('原生 App 尚未完成 initNativeApp，拿不到 build 号')
  return { [NATIVE_CLIENT_HEADER]: `ios/${nativeBuild}` }
}

/**
 * 原生壳启动初始化：记下 build 号、退出旧测试服会话、铺满安全区。
 * 必须在 i18n、api 等任何会读服务器选择的模块导入之前 await 完成；浏览器里直接返回。
 */
export async function initNativeApp(): Promise<void> {
  if (!isNativeApp()) return
  const info = await App.getInfo()
  if (!info.build) throw new Error('App.getInfo 返回空 build 号，原生工程缺少 CFBundleVersion')
  nativeBuild = info.build
  const viewport = document.querySelector('meta[name="viewport"]')
  if (!viewport) throw new Error('index.html 缺少 viewport meta')
  // maximum-scale=1：iOS 聚焦输入框时不再把整页放大（浏览器版仍允许缩放，这里只改原生壳）
  viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, viewport-fit=cover')
  await StatusBar.setStyle({ style: Style.Dark })
}

export async function readNativeRefreshToken(): Promise<string | null> {
  return SecureStorage.getItem(REFRESH_TOKEN_KEY)
}

export async function storeNativeRefreshToken(token: string): Promise<void> {
  await SecureStorage.setItem(REFRESH_TOKEN_KEY, token)
}

export async function clearNativeRefreshToken(): Promise<void> {
  await SecureStorage.removeItem(REFRESH_TOKEN_KEY)
}

/** 原生退出登录送给 `/auth/logout` 的请求体：refresh token 证明身份，顺带停用本机推送设备。 */
export interface PendingNativeLogout {
  refresh_token: string
  push_device_token: string | null
}

export async function readPendingNativeLogout(): Promise<PendingNativeLogout | null> {
  const raw = await SecureStorage.getItem(PENDING_LOGOUT_KEY)
  if (raw === null) return null
  if (typeof raw !== 'string') throw new Error(`Keychain 里的 ${PENDING_LOGOUT_KEY} 不是字符串`)
  const parsed: unknown = JSON.parse(raw)
  if (!parsed || typeof parsed !== 'object') throw new Error(`Keychain 里的 ${PENDING_LOGOUT_KEY} 格式错误`)
  const { refresh_token, push_device_token } = parsed as Record<string, unknown>
  if (typeof refresh_token !== 'string' || !refresh_token) throw new Error(`Keychain 里的 ${PENDING_LOGOUT_KEY} 缺少 refresh_token`)
  return { refresh_token, push_device_token: typeof push_device_token === 'string' ? push_device_token : null }
}

export async function storePendingNativeLogout(entry: PendingNativeLogout): Promise<void> {
  await SecureStorage.setItem(PENDING_LOGOUT_KEY, JSON.stringify(entry))
}

export async function clearPendingNativeLogout(): Promise<void> {
  await SecureStorage.removeItem(PENDING_LOGOUT_KEY)
}
